/**
 * PersonalboqenWizard.jsx – Digitaler HR-Onboarding Wizard
 * 3-Wege-Flow: "Vor Ort" | "Link versenden" | "Bestehende Mitarbeiter"
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  ChevronRight, CheckCircle2, Link as LinkIcon, Copy, Loader2,
  User, Building2, ClipboardList, Check, Edit2, Eye, FileText,
  Mail, Users, Send, UserCheck, ArrowLeft,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Kleine Hilfskomponenten ──────────────────────────────────────────────────

function YesNo({ label, value, onChange }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      <div className="flex gap-2">
        {[['Ja', true], ['Nein', false]].map(([txt, val]) => (
          <button key={txt} type="button" onClick={() => onChange(val)}
            className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-all ${value === val ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:border-muted-foreground/50'}`}>
            {txt}
          </button>
        ))}
      </div>
    </div>
  );
}

function DocCheckbox({ label, checked, onChange }) {
  return (
    <div className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${checked ? 'border-green-400 bg-green-50 dark:bg-green-900/20' : 'border-border'}`}
      onClick={() => onChange(!checked)}>
      <div className={`h-5 w-5 rounded border-2 flex items-center justify-center shrink-0 ${checked ? 'border-green-500 bg-green-500' : 'border-muted-foreground'}`}>
        {checked && <Check className="h-3.5 w-3.5 text-white" />}
      </div>
      <span className="text-sm">{label} – liegt vor</span>
    </div>
  );
}

const emptyAG = () => ({
  firma: '', location_id: '', eintrittsdatum: '', berufsbezeichnung: '',
  ausgeubte_taetigkeit: '', kostenstelle: '', abt_nummer: '',
  arbeitszeit: '',
  hauptbeschaeftigung: null,
  nebenbeschaeftigung: null,
  probezeit: null,
  probezeit_dauer: '',
  befristet: null,
  befristung_bis: '',
  elterneigenschaft: false,
  stunden_pro_woche: '',
  stunden_pro_monat: '',
  urlaubstage: '20',
  arbeitstage_woche: '5',
  vergutung: '',
  doc_arbeitsvertrag: false,
  doc_sv_ausweis: false,
  doc_kk_bescheinigung: false,
  doc_lohnsteuer: false,
  doc_vwl: false,
  doc_elterneigenschaft: false,
});

// ── Flow-Auswahl ─────────────────────────────────────────────────────────────

const FLOW_OPTIONS = [
  {
    id: 'link_versenden',
    title: 'Link versenden',
    subtitle: 'Mitarbeiter füllt selbst aus',
    description: 'Admin trägt Arbeitgeber-Daten ein. Mitarbeiter erhält einen sicheren Link und füllt seine persönlichen Daten selbst aus. PIN wird persönlich mitgeteilt.',
    icon: Send,
    color: 'from-blue-500 to-blue-600',
    bgHover: 'hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-blue-950/30',
    tag: 'Standard',
    tagColor: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  },
  {
    id: 'vor_ort',
    title: 'Vor Ort',
    subtitle: 'Mitarbeiter ist anwesend',
    description: 'Admin und Mitarbeiter füllen alle Felder gemeinsam aus. Beide Unterschriften werden sofort digital erfasst.',
    icon: Users,
    color: 'from-green-500 to-green-600',
    bgHover: 'hover:border-green-400 hover:bg-green-50/50 dark:hover:bg-green-950/30',
    tag: null,
    tagColor: '',
  },
  {
    id: 'bestehender_mitarbeiter',
    title: 'Bestehende Mitarbeiter',
    subtitle: 'Nachträgliche Erfassung',
    description: 'Für bereits beschäftigte Mitarbeiter. Das Eintrittsdatum kann rückwirkend eingetragen werden. Die Unterschrift wird mit dem heutigen Datum erfasst (Zoll-konform).',
    icon: UserCheck,
    color: 'from-violet-500 to-violet-600',
    bgHover: 'hover:border-violet-400 hover:bg-violet-50/50 dark:hover:bg-violet-950/30',
    tag: 'Zoll-Compliance',
    tagColor: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
  },
];

function FlowSelectionScreen({ onSelect, isExistingEmployee = false }) {
  // Für Bestandsmitarbeiter: nur Vor Ort + Link (kein "Bestehender Mitarbeiter" nötig)
  const options = isExistingEmployee
    ? FLOW_OPTIONS.filter(o => o.id !== 'bestehender_mitarbeiter').map(opt => ({
        ...opt,
        description: opt.id === 'vor_ort'
          ? 'Admin und Mitarbeiter füllen alle Felder gemeinsam aus. Beide Unterschriften werden sofort digital erfasst. Das Eintrittsdatum darf rückwirkend sein.'
          : 'Mitarbeiter erhält einen sicheren Link. Admin füllt die Arbeitgeber-Daten aus. PIN wird persönlich mitgeteilt. Das Eintrittsdatum darf rückwirkend sein.',
      }))
    : FLOW_OPTIONS;
  return (
    <div className="space-y-3 py-2">
      <p className="text-sm text-muted-foreground">
        {isExistingEmployee
          ? 'Wie soll der Personalbogen für diesen Mitarbeiter erstellt werden? Das Eintrittsdatum darf rückwirkend eingetragen werden.'
          : 'Wie soll der Personalbogen ausgefüllt werden?'}
      </p>
      <div className="space-y-3">
        {options.map((opt) => {
          const Icon = opt.icon;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => onSelect(opt.id)}
              className={`w-full text-left p-4 rounded-xl border-2 border-border transition-all group ${opt.bgHover}`}
              data-testid={`flow-select-${opt.id}`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${opt.color} flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform`}>
                  <Icon className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-foreground">{opt.title}</p>
                    {opt.tag && (
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${opt.tagColor}`}>
                        {opt.tag}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{opt.subtitle}</p>
                  <p className="text-xs text-muted-foreground/70 mt-1 leading-relaxed">{opt.description}</p>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Haupt-Wizard ─────────────────────────────────────────────────────────────

export function PersonalboqenWizard({ open, onClose, onCreated, locations = [], existingEmployee = null }) {
  const [flowType, setFlowType] = useState(null);
  const [step, setStep] = useState(1);
  const [ag, setAg] = useState(emptyAG());
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  const [enableVerification, setEnableVerification] = useState(false); // NEU: KI-Verifikation
  // Wenn existingEmployee: Name + Email fest vorausgefüllt
  const [mitarbeiterName, setMitarbeiterName] = useState(existingEmployee?.name || '');
  const [mitarbeiterEmail, setMitarbeiterEmail] = useState(existingEmployee?.email || '');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewText, setPreviewText] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const appBase = window.location.origin;

  useEffect(() => {
    if (open) {
      axios.get(`${API}/api/contracts/templates`, { headers })
        .then(r => setTemplates(r.data || []))
        .catch(() => {});
    }
  }, [open]);

  const handleClose = () => {
    // Wenn ein Bogen über link_versenden erstellt wurde und der User jetzt schließt,
    // Parent-Liste erst JETZT refreshen (nach dem Schließen des Dialogs)
    const wasCreatedLinkFlow = !!created && step === 2;

    setFlowType(null);
    setStep(1);
    setAg(emptyAG());
    setCreated(null);
    setCopied(false);
    setSelectedTemplateId('');
    setShowPreview(false);
    setMitarbeiterName(existingEmployee?.name || '');
    setMitarbeiterEmail(existingEmployee?.email || '');
    setEmailSending(false);
    setEmailSent(false);
    onClose();

    // Refresh erst nach close (nicht während Step 2 aktiv)
    if (wasCreatedLinkFlow) onCreated?.(null);
  };

  const handleFlowSelect = (type) => {
    setFlowType(type);
    setStep(1);
  };

  const handleSendEmail = async () => {
    if (!created?.token) return;
    const emailAddr = mitarbeiterEmail.trim() || created?.mitarbeiter_email;
    if (!emailAddr) { toast.error('Keine E-Mail-Adresse vorhanden'); return; }
    setEmailSending(true);
    try {
      await axios.post(
        `${API}/api/personalbogen/public/${created.token}/send-pin-email`,
        { email: emailAddr },
        { headers }
      );
      setEmailSent(true);
      toast.success(`Link wurde an ${emailAddr} gesendet!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'E-Mail konnte nicht gesendet werden');
    } finally {
      setEmailSending(false);
    }
  };

  const handleCreate = async () => {
    if (!existingEmployee && !mitarbeiterName.trim()) { toast.error('Name des Mitarbeiters ist Pflicht'); return; }
    if (!ag.vergutung) { toast.error('Vergütung (Lohn) ist Pflicht'); return; }
    if (!ag.eintrittsdatum) { toast.error('Eintrittsdatum ist Pflicht'); return; }
    if (!ag.ausgeubte_taetigkeit) { toast.error('Ausgeübte Tätigkeit ist Pflicht'); return; }
    if (!ag.arbeitszeit) { toast.error('Bitte Beschäftigungsart auswählen'); return; }
    if (ag.hauptbeschaeftigung === null) { toast.error('Bitte Hauptbeschäftigung Ja/Nein angeben'); return; }
    if (!ag.hauptbeschaeftigung && ag.nebenbeschaeftigung === null) { toast.error('Bitte Nebenbeschäftigung Ja/Nein angeben'); return; }
    if (ag.befristet === null) { toast.error('Bitte Befristung Ja/Nein angeben'); return; }
    if (ag.probezeit === null) { toast.error('Bitte Probezeit Ja/Nein angeben'); return; }

    setSaving(true);
    try {
      const res = await axios.post(`${API}/api/personalbogen`, {
        arbeitgeber: ag,
        mitarbeiter_name: mitarbeiterName.trim(),
        mitarbeiter_email: mitarbeiterEmail.trim() || undefined,
        location_id: ag.location_id || undefined,
        flow_type: existingEmployee ? 'bestehender_mitarbeiter' : flowType,
        ...(existingEmployee ? { employee_id: existingEmployee.id } : {}),
        enable_document_verification: flowType === 'link_versenden' ? enableVerification : false,
      }, { headers });
      const newBogen = res.data;
      setCreated(newBogen);

      if (selectedTemplateId && newBogen.id) {
        try {
          await axios.post(`${API}/api/contracts/signing/create`, {
            personalbogen_id: newBogen.id,
            template_id: selectedTemplateId,
          }, { headers });
        } catch (e) {
          toast.warning('Personalbogen erstellt, Vertrag konnte nicht zugeordnet werden');
        }
      }

      if (flowType === 'link_versenden') {
        // Wichtig: onCreated(null) NICHT hier aufrufen – würde Parent-re-render triggern
        // und den Wizard-State (Step 2) zurücksetzen. Refresh passiert beim Schließen.
        setStep(2);
        toast.success('Personalbogen angelegt!');
      } else {
        // Vor Ort / Bestehende Mitarbeiter: EditModal direkt öffnen
        toast.success('Personalbogen angelegt! Bitte jetzt alle Daten ausfüllen.');
        if (onCreated) onCreated(newBogen);
        handleClose();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Erstellen');
    } finally { setSaving(false); }
  };

  const handlePreview = async () => {
    if (!created?.id) return;
    setPreviewLoading(true);
    setShowPreview(true);
    try {
      const r = await axios.get(`${API}/api/contracts/public/${created.token}`);
      if (r.data.has_contract) {
        setPreviewText(r.data.text_snapshot || 'Kein Vertragstext verfügbar');
      } else {
        setPreviewText('Diesem Bogen wurde kein Vertrag zugeordnet.');
      }
    } catch {
      setPreviewText('Vorschau nicht verfügbar.');
    } finally { setPreviewLoading(false); }
  };

  const publicLink = created ? `${appBase}/personalbogen/${created.token}` : '';

  const handleCopy = () => {
    navigator.clipboard.writeText(publicLink);
    setCopied(true);
    toast.success('Link kopiert!');
    setTimeout(() => setCopied(false), 3000);
  };

  const flowLabel = {
    vor_ort: 'Vor Ort',
    link_versenden: 'Link versenden',
    bestehender_mitarbeiter: 'Bestehende Mitarbeiter',
  }[flowType] || '';

  const isVorOrtType = flowType === 'vor_ort' || flowType === 'bestehender_mitarbeiter';

  // Urlaubsberechnung anzeigen
  const calcUrlaub = () => {
    const days = parseInt(ag.arbeitstage_woche);
    const base = parseInt(ag.urlaubstage);
    if (!days || !base || days === 5) return null;
    return Math.round((base / 5) * days);
  };
  const urlaubCalc = calcUrlaub();

  return (
    <>
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg" data-testid="personalbogen-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-primary" />
            Digitaler Personalbogen
            {flowType && (
              <span className="ml-auto text-xs font-normal text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                {flowLabel}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* ── FLOW AUSWAHL ── */}
        {!flowType && (
          <FlowSelectionScreen onSelect={handleFlowSelect} isExistingEmployee={!!existingEmployee} />
        )}

        {/* ── SCHRITT-INDICATOR (nur bei Link-Flow) ── */}
        {flowType === 'link_versenden' && (
          <div className="flex items-center gap-2 py-1">
            {[
              { id: 1, title: 'Arbeitgeber-Daten', icon: Building2 },
              { id: 2, title: 'Link & PIN', icon: LinkIcon },
            ].map((s, i, arr) => (
              <React.Fragment key={s.id}>
                <div className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full transition-all ${
                  step === s.id ? 'bg-primary text-primary-foreground'
                    : step > s.id ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                    : 'bg-muted text-muted-foreground'
                }`}>
                  {step > s.id ? <CheckCircle2 className="h-3.5 w-3.5" /> : <s.icon className="h-3.5 w-3.5" />}
                  {s.title}
                </div>
                {i < arr.length - 1 && <div className="h-px flex-1 bg-border" />}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Vor-Ort / Bestehender: Einzeiliger Hinweis */}
        {isVorOrtType && step === 1 && (
          <div className={`flex items-start gap-2 px-3 py-2 rounded-lg text-xs ${
            flowType === 'bestehender_mitarbeiter'
              ? 'bg-violet-50 dark:bg-violet-950/30 text-violet-700 dark:text-violet-300 border border-violet-200 dark:border-violet-800'
              : 'bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
          }`}>
            {flowType === 'bestehender_mitarbeiter' ? (
              <><UserCheck className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>Das <strong>Eintrittsdatum darf rückwirkend</strong> sein. Die Unterschrift wird automatisch mit dem heutigen Datum erfasst (Zoll-Compliance).</span></>
            ) : (
              <><Users className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>Nach dem Anlegen öffnet sich direkt das vollständige Formular zum gemeinsamen Ausfüllen mit dem Mitarbeiter.</span></>
            )}
          </div>
        )}

        {/* ── SCHRITT 1: Arbeitgeber-Daten ── */}
        {flowType && step === 1 && (
          <div className="space-y-4 py-1 max-h-[65vh] overflow-y-auto pr-1">

            {/* Zurück zur Flow-Auswahl */}
            <button
              type="button"
              onClick={() => { setFlowType(null); setAg(emptyAG()); setMitarbeiterName(''); setMitarbeiterEmail(''); }}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Andere Erfassungsart wählen
            </button>

            {/* Mitarbeiter-Block */}
            <div className="border border-primary/30 bg-primary/5 rounded-xl p-4 space-y-3">
              <p className="text-xs font-semibold text-primary uppercase tracking-wide">Mitarbeiter</p>
              {existingEmployee ? (
                /* Bestandsmitarbeiter: Read-Only Anzeige */
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center text-white font-bold text-sm shrink-0">
                    {(existingEmployee.name || existingEmployee.email || '?')[0].toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{existingEmployee.name || existingEmployee.email}</p>
                    <p className="text-xs text-muted-foreground">{existingEmployee.email}</p>
                  </div>
                  <span className="ml-auto text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 px-2 py-0.5 rounded-full">Bestandsmitarbeiter</span>
                </div>
              ) : (
                /* Neuer Mitarbeiter: Eingabefelder */
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label>Name des Mitarbeiters <span className="text-destructive">*</span></Label>
                    <Input
                      placeholder="z.B. Max Mustermann"
                      value={mitarbeiterName}
                      onChange={e => setMitarbeiterName(e.target.value)}
                      data-testid="mitarbeiter-name-input"
                    />
                  </div>
                  {flowType === 'link_versenden' && (
                    <div className="space-y-1">
                      <Label>E-Mail-Adresse <span className="text-muted-foreground font-normal text-xs">(optional – für Link-Versand)</span></Label>
                      <Input
                        type="email"
                        placeholder="mitarbeiter@beispiel.de"
                        value={mitarbeiterEmail}
                        onChange={e => setMitarbeiterEmail(e.target.value)}
                        data-testid="mitarbeiter-email-input"
                      />
                    </div>
                  )}
                </div>
              )}
              {/* Bei Bestandsmitarbeiter + Link-Versand: E-Mail optional überschreiben */}
              {existingEmployee && flowType === 'link_versenden' && (
                <div className="space-y-1 border-t pt-2">
                  <Label className="text-xs">E-Mail für Link-Versand <span className="text-muted-foreground font-normal">(vorausgefüllt)</span></Label>
                  <Input
                    type="email"
                    placeholder={existingEmployee.email}
                    value={mitarbeiterEmail}
                    onChange={e => setMitarbeiterEmail(e.target.value)}
                    data-testid="mitarbeiter-email-input"
                  />
                </div>
              )}

              {/* KI-Verifikation Checkbox – nur bei Link-Versenden */}
              {flowType === 'link_versenden' && (
                <div className="border border-blue-200 dark:border-blue-800 rounded-xl p-3 bg-blue-50 dark:bg-blue-900/10">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={enableVerification}
                      onChange={e => setEnableVerification(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300"
                      data-testid="enable-verification-checkbox"
                    />
                    <div>
                      <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">
                        KI-Dokument-Verifikation aktivieren
                      </p>
                      <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                        Mitarbeiter kann optional Ausweis + Bankkarte fotografieren. KI prüft ob die eingetippten Daten korrekt sind. <strong>Bilder werden nicht gespeichert.</strong>
                      </p>
                    </div>
                  </label>
                </div>
              )}
            </div>

            {/* Standort / Firma */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1 col-span-2">
                <Label>Beschäftigungsbetrieb (Standort) *</Label>
                {locations.length > 0 ? (
                  <div className="space-y-2">
                    <select
                      value={ag.location_id}
                      onChange={e => {
                        const loc = locations.find(l => l.id === e.target.value);
                        setAg(a => ({
                          ...a,
                          location_id: e.target.value,
                          firma: loc ? (loc.legal_name || loc.name || '') : '',
                        }));
                      }}
                      className="h-9 w-full rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                      data-testid="ag-firma-select"
                    >
                      <option value="">Standort auswählen…</option>
                      {locations.map(loc => (
                        <option key={loc.id} value={loc.id}>
                          {loc.legal_name || loc.name}
                          {loc.legal_name && loc.name && loc.legal_name !== loc.name ? ` (${loc.name})` : ''}
                        </option>
                      ))}
                    </select>
                    {ag.location_id && !locations.find(l => l.id === ag.location_id)?.legal_name && (
                      <p className="text-xs text-amber-600 dark:text-amber-400">
                        Für diesen Standort sind noch keine Firmendaten hinterlegt.
                        Bitte unter <strong>Personal → Firmenprofil</strong> ergänzen.
                      </p>
                    )}
                  </div>
                ) : (
                  <Input
                    placeholder="z.B. JET Fellbach"
                    value={ag.firma}
                    onChange={e => setAg(a => ({ ...a, firma: e.target.value }))}
                    data-testid="ag-firma-input"
                  />
                )}
              </div>

              <div className="space-y-1">
                <Label>Eintrittsdatum *
                  {flowType === 'bestehender_mitarbeiter' && (
                    <span className="ml-1 text-xs font-normal text-violet-600 dark:text-violet-400">(darf rückwirkend sein)</span>
                  )}
                </Label>
                <Input
                  type="date"
                  value={ag.eintrittsdatum}
                  onChange={e => setAg(a => ({ ...a, eintrittsdatum: e.target.value }))}
                  data-testid="ag-eintrittsdatum-input"
                />
              </div>
              <div className="space-y-1">
                <Label>Berufsbezeichnung</Label>
                <Input
                  placeholder="z.B. Tankwart"
                  value={ag.berufsbezeichnung}
                  onChange={e => setAg(a => ({ ...a, berufsbezeichnung: e.target.value }))}
                />
              </div>
              <div className="space-y-1 col-span-2">
                <Label>Ausgeübte Tätigkeit *</Label>
                <Input
                  placeholder="z.B. Kassierer / Verkauf"
                  value={ag.ausgeubte_taetigkeit}
                  onChange={e => setAg(a => ({ ...a, ausgeubte_taetigkeit: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label>Kostenstelle</Label>
                <Input placeholder="z.B. KST-001" value={ag.kostenstelle}
                  onChange={e => setAg(a => ({ ...a, kostenstelle: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Abt.-Nummer</Label>
                <Input value={ag.abt_nummer}
                  onChange={e => setAg(a => ({ ...a, abt_nummer: e.target.value }))} />
              </div>
            </div>

            {/* Vertragsdetails */}
            <div className="border-t pt-4 space-y-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Vertragsdetails</p>

              {/* Vertragsvorlage */}
              {templates.length > 0 && (
                <div className="space-y-1.5">
                  <Label className="text-sm flex items-center gap-1.5">
                    <FileText className="h-3.5 w-3.5 text-indigo-600" />
                    Vertragsvorlage zuweisen
                    <span className="text-muted-foreground font-normal">(optional)</span>
                  </Label>
                  <select
                    value={selectedTemplateId}
                    onChange={e => setSelectedTemplateId(e.target.value)}
                    className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                    data-testid="template-select"
                  >
                    <option value="">– Kein Vertrag –</option>
                    {templates.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Beschäftigungsart */}
              <div className="space-y-1.5">
                <Label className="text-sm">Beschäftigungsart *</Label>
                <div className="flex gap-2">
                  {[['Vollzeit', 'vollzeit'], ['Teilzeit', 'teilzeit'], ['Aushilfe (Minijob)', 'minijob'], ['Werkstudent', 'werkstudent']].map(([txt, val]) => (
                    <button key={val} type="button" onClick={() => setAg(a => ({ ...a, arbeitszeit: val }))}
                      className={`flex-1 py-2 text-sm rounded-lg border font-medium transition-all ${ag.arbeitszeit === val ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'}`}
                      data-testid={`arbeitszeit-${val}`}>
                      {txt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Vergütung */}
              <div className="space-y-1.5">
                <Label className="text-sm">Brutto-Vergütung <span className="text-destructive">*</span></Label>
                <Input
                  type="text"
                  placeholder="z.B. 14,00 €/Std. oder 800,00 €/Monat"
                  value={ag.vergutung}
                  onChange={e => setAg(a => ({ ...a, vergutung: e.target.value }))}
                  data-testid="vergutung-input"
                />
                {ag.arbeitszeit === 'minijob' && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">Minijob-Grenze: 556 €/Monat (ab 2025)</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <YesNo label="Hauptbeschäftigung? *" value={ag.hauptbeschaeftigung}
                  onChange={v => setAg(a => ({ ...a, hauptbeschaeftigung: v, nebenbeschaeftigung: v ? null : a.nebenbeschaeftigung }))} />
                <YesNo label="Elterneigenschaft?" value={ag.elterneigenschaft}
                  onChange={v => setAg(a => ({ ...a, elterneigenschaft: v }))} />
              </div>
              {ag.hauptbeschaeftigung === false && (
                <YesNo label="Nebenbeschäftigung? *" value={ag.nebenbeschaeftigung}
                  onChange={v => setAg(a => ({ ...a, nebenbeschaeftigung: v }))} />
              )}

              {/* Arbeitszeit in Stunden */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-sm">Stunden / Woche</Label>
                  <div className="flex items-center gap-2">
                    <Input type="number" min="1" max="60" step="0.5"
                      value={ag.stunden_pro_woche}
                      onChange={e => setAg(a => ({ ...a, stunden_pro_woche: e.target.value }))}
                      placeholder={ag.arbeitszeit === 'minijob' ? 'z.B. 10' : 'z.B. 40'}
                      className="w-24" data-testid="stunden-woche-input" />
                    <span className="text-xs text-muted-foreground">Std/Wo</span>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">Stunden / Monat</Label>
                  <div className="flex items-center gap-2">
                    <Input type="number" min="1" max="260" step="1"
                      value={ag.stunden_pro_monat}
                      onChange={e => setAg(a => ({ ...a, stunden_pro_monat: e.target.value }))}
                      placeholder="z.B. 173" className="w-24" data-testid="stunden-monat-input" />
                    <span className="text-xs text-muted-foreground">Std/Mo</span>
                  </div>
                </div>
              </div>

              {/* Urlaub & Arbeitstage */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-sm">Jahresurlaubstage</Label>
                  <div className="flex items-center gap-2">
                    <Input type="number" min="1" max="40" step="1"
                      value={ag.urlaubstage}
                      onChange={e => setAg(a => ({ ...a, urlaubstage: e.target.value }))}
                      placeholder="20" className="w-24" data-testid="urlaubstage-input" />
                    <span className="text-xs text-muted-foreground">Tage</span>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">Arbeitstage / Woche</Label>
                  <div className="flex items-center gap-2">
                    <Input type="number" min="1" max="7" step="1"
                      value={ag.arbeitstage_woche || ''}
                      onChange={e => {
                        const d = e.target.value;
                        // Minijob: Auto-Urlaub = Arbeitstage × 4
                        const autoUrlaub = ag.arbeitszeit === 'minijob' && d
                          ? String(Math.max(1, parseInt(d, 10) * 4))
                          : ag.urlaubstage;
                        setAg(a => ({ ...a, arbeitstage_woche: d, urlaubstage: autoUrlaub }));
                      }}
                      placeholder="5" className="w-24" data-testid="arbeitstage-woche-input" />
                    <span className="text-xs text-muted-foreground">Tage</span>
                  </div>
                  {ag.arbeitszeit === 'minijob' && ag.arbeitstage_woche && (
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      Minijob: {ag.arbeitstage_woche} × 4 = <strong>{parseInt(ag.arbeitstage_woche) * 4} Tage</strong>
                    </p>
                  )}
                  {urlaubCalc && ag.arbeitszeit !== 'minijob' && (
                    <p className="text-xs text-muted-foreground">
                      Anteilig: {ag.urlaubstage}/5×{ag.arbeitstage_woche} = <strong>{urlaubCalc} Tage</strong>
                    </p>
                  )}
                </div>
              </div>

              <YesNo label="Probezeit? *" value={ag.probezeit}
                onChange={v => setAg(a => ({ ...a, probezeit: v }))} />
              {ag.probezeit && (
                <div className="space-y-1">
                  <Label className="text-sm">Probezeit-Dauer</Label>
                  <Input value={ag.probezeit_dauer}
                    onChange={e => setAg(a => ({ ...a, probezeit_dauer: e.target.value }))}
                    placeholder="z.B. 3 Monate" />
                </div>
              )}
              <YesNo label="Befristetes Arbeitsverhältnis? *" value={ag.befristet}
                onChange={v => setAg(a => ({ ...a, befristet: v }))} />
              {ag.befristet && (
                <div className="space-y-1">
                  <Label className="text-sm">Befristet bis</Label>
                  <Input type="date" value={ag.befristung_bis}
                    onChange={e => setAg(a => ({ ...a, befristung_bis: e.target.value }))} />
                </div>
              )}
            </div>

            {/* Arbeitspapiere Checkliste */}
            <div className="border-t pt-4 space-y-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Arbeitspapiere vorhanden?</p>
              {[
                ['doc_arbeitsvertrag', 'Arbeitsvertrag'],
                ['doc_sv_ausweis', 'SV-Ausweis'],
                ['doc_kk_bescheinigung', 'KK-Mitgliedsbescheinigung'],
                ['doc_lohnsteuer', 'Lohnsteuerbescheinigung (Vorjahr)'],
                ['doc_vwl', 'VWL-Vertrag'],
                ['doc_elterneigenschaft', 'Nachweis Elterneigenschaft'],
              ].map(([field, label]) => (
                <DocCheckbox key={field} label={label} checked={!!ag[field]}
                  onChange={v => setAg(a => ({ ...a, [field]: v }))} />
              ))}
            </div>

            <div className="flex justify-between pt-2 gap-2">
              <Button variant="outline" onClick={handleClose} className="flex-1">Abbrechen</Button>
              <Button
                onClick={handleCreate}
                disabled={saving || (!existingEmployee && !mitarbeiterName.trim()) || !ag.vergutung || !ag.eintrittsdatum || !ag.ausgeubte_taetigkeit || !ag.arbeitszeit || ag.hauptbeschaeftigung === null || ag.befristet === null || ag.probezeit === null}
                className="flex-1"
                data-testid="create-bogen-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {isVorOrtType ? (
                  <><Users className="h-4 w-4 mr-1.5" />Anlegen & Ausfüllen</>
                ) : (
                  <>Link generieren <ChevronRight className="h-4 w-4 ml-1" /></>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* ── SCHRITT 2: Link teilen (nur link_versenden) ── */}
        {flowType === 'link_versenden' && step === 2 && created && (
          <div className="space-y-5 py-2 max-h-[65vh] overflow-y-auto pr-1">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4 flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-green-800 dark:text-green-300 text-sm">
                  Personalbogen angelegt für <strong>{mitarbeiterName}</strong>!
                </p>
                <p className="text-xs text-green-700 dark:text-green-400 mt-0.5">
                  Link ist 7 Tage gültig
                </p>
              </div>
            </div>

            {/* PIN – nur für Admin sichtbar (DSGVO: nicht in E-Mail) */}
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3">
              <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-1">
                PIN-Code (nur für Admin)
              </p>
              <p className="text-2xl font-bold font-mono tracking-[0.3em] text-amber-800 dark:text-amber-300">
                {created.pin_code || '––––––'}
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-500 mt-1">
                Aus Datenschutzgründen (DSGVO) wird der PIN-Code <strong>nicht per E-Mail</strong> gesendet.
                Bitte teile den PIN dem Mitarbeiter <strong>persönlich</strong> mit.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Link für den Mitarbeiter</Label>
              <div className="flex gap-2">
                <Input value={publicLink} readOnly className="font-mono text-xs bg-muted" data-testid="public-link-input" />
                <Button variant="outline" size="icon" onClick={handleCopy} data-testid="copy-link-btn">
                  {copied ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {/* Teilen-Buttons */}
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                className="gap-2 border-green-300 text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
                onClick={() => {
                  const msg = `Hallo ${mitarbeiterName}! 👋\n\nBitte fülle deinen digitalen Personalfragebogen aus:\n\n🔗 Link: ${publicLink}\n🔑 PIN: ${created.pin_code}\n\nDer Link ist 7 Tage gültig.`;
                  window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
                }}
                data-testid="whatsapp-share-btn"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current shrink-0"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                Per WhatsApp
              </Button>
              <Button variant="outline" className="gap-2" onClick={handleCopy} data-testid="copy-link-btn-2">
                {copied ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                Link kopieren
              </Button>
            </div>

            {/* E-Mail senden (OHNE PIN) */}
            {(mitarbeiterEmail.trim() || created?.mitarbeiter_email) && (
              <Button
                variant={emailSent ? 'outline' : 'default'}
                className={`w-full gap-2 ${emailSent ? 'border-green-400 text-green-700' : ''}`}
                onClick={handleSendEmail}
                disabled={emailSending || emailSent}
                data-testid="send-email-btn"
              >
                {emailSending ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Wird gesendet...</>
                ) : emailSent ? (
                  <><CheckCircle2 className="h-4 w-4 text-green-500" /> E-Mail gesendet an {mitarbeiterEmail || created?.mitarbeiter_email}</>
                ) : (
                  <><Mail className="h-4 w-4" /> Link per E-Mail senden (ohne PIN)</>
                )}
              </Button>
            )}

            <p className="text-xs text-muted-foreground text-center bg-muted/40 rounded-lg p-2">
              Die E-Mail enthält nur den Link. Den PIN-Code teilst du dem Mitarbeiter persönlich mit.
            </p>

            {/* Vertragsvorschau */}
            {selectedTemplateId && (
              <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-xl p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-indigo-600" />
                    <p className="text-sm font-medium text-indigo-800 dark:text-indigo-300">
                      Vorlage: <strong>{templates.find(t => t.id === selectedTemplateId)?.name}</strong>
                    </p>
                  </div>
                  <Button size="sm" variant="outline" onClick={handlePreview}
                    className="gap-1.5 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                    data-testid="preview-contract-btn">
                    <Eye className="h-3.5 w-3.5" />
                    Vorschau
                  </Button>
                </div>
              </div>
            )}

            <div className="bg-muted/50 rounded-lg p-3 space-y-1 text-xs text-muted-foreground">
              <p className="font-medium text-foreground">Nächste Schritte:</p>
              <p>1. Link + PIN dem Mitarbeiter mitteilen (Link per E-Mail / WhatsApp, PIN mündlich)</p>
              <p>2. Mitarbeiter öffnet Link → gibt PIN ein → füllt Formular aus → unterschreibt</p>
              <p>3. Du siehst "Zur Prüfung" in der Mitarbeiter-Liste</p>
              <p>4. PDF herunterladen + Vertrag separat erstellen + Account anlegen</p>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleClose}>Schließen</Button>
              <Button
                variant="outline"
                className="gap-2 border-violet-300 text-violet-700 hover:bg-violet-50 dark:hover:bg-violet-900/20"
                onClick={() => { onCreated && onCreated(created); handleClose(); }}
                data-testid="fill-directly-btn"
              >
                <Edit2 className="h-4 w-4" />
                Mitarbeiter ist vor Ort – Jetzt ausfüllen
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* Vertragsvorschau Modal */}
    <Dialog open={showPreview} onOpenChange={setShowPreview}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-5 pb-3 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-indigo-600" />
            Vertragsvorschau – {templates.find(t => t.id === selectedTemplateId)?.name}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {previewLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <pre className="text-sm text-foreground whitespace-pre-wrap font-sans leading-relaxed">{previewText}</pre>
          )}
        </div>
        <DialogFooter className="px-6 py-4 border-t shrink-0">
          <Button variant="outline" onClick={() => setShowPreview(false)}>Schließen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
